import { useCallback, useEffect, useState, useRef } from "react";
import axios, { AxiosRequestConfig } from "axios";
import { Auth } from "firebase/auth";

import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, APIHandler } from "../../../api/base";
import { logout } from "../../../api/auth/endpoints-referrers";
import { TokenResponse } from "../../interfaces/auth/token-response.interface";
import { stringGetter } from "../../utilities/localstorage-dealer/localstorage-getters.util";
import {
    setDataToLocalStorage,
    removeDataToLocalStorage
} from "../../utilities/localstorage-dealer/localstorage-setter.util";

interface RetryQueueItem {
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
    config: AxiosRequestConfig;
}

/**
 * Recognize 401s that a fresh Firebase token would actually fix. Anything
 * else (user-level errors like "User not found in this tenant") must be
 * propagated as-is — refreshing the token can't change the answer, and
 * refreshing in a loop trips Firebase's per-project token-refresh quota,
 * which then locks out every other user of the same project for hours.
 *
 * Backend 401 shapes:
 *   - Firebase token expired / invalid signature / revoked → `Token validation failed: ...`
 *     (from FirebaseStrategy.validate) — refreshable
 *   - User row missing for the token's email → `User not found. Email: ...`
 *     (from UserPermissionsService.getFirebaseUser) — NOT refreshable, surface to caller
 *   - Embed session guard rejection → no body — refreshable (treated as generic)
 */
function isRefreshableAuthFailure(error: any): boolean {
    const msg = String(error?.response?.data?.message ?? "").toLowerCase();
    if (!msg) return true; // empty body = unknown; safest is to try a refresh
    if (msg.includes("user not found")) return false;
    if (msg.includes("no user record")) return false;
    return true;
}

// Axios marks the request config object as "already retried after a token
// refresh" so a second 401 on the same request can't kick off another
// refresh-retry cycle. Without this, a deterministic backend 401 (e.g. a
// case-mismatched user lookup) loops indefinitely and exhausts the Firebase
// token-refresh quota.
const REFRESH_RETRY_FLAG = "__mbassistRefreshRetried";

// Firebase REST API for token refresh (works without client SDK session)
const FIREBASE_TOKEN_REFRESH_URL = "https://securetoken.googleapis.com/v1/token";

/**
 * Refresh Firebase token using REST API
 * This works even when auth.currentUser is null (e.g., on tenant domain after auth domain login)
 */
async function refreshTokenViaRestApi(
    refreshToken: string,
    apiKey: string
): Promise<{ accessToken: string; refreshToken: string }> {
    const response = await axios.post(
        `${FIREBASE_TOKEN_REFRESH_URL}?key=${apiKey}`,
        new URLSearchParams({
            grant_type: "refresh_token",
            refresh_token: refreshToken
        }),
        {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            }
        }
    );

    return {
        accessToken: response.data.id_token,
        refreshToken: response.data.refresh_token
    };
}

export function useFirebaseToken(auth: Auth, onTokenInvalid: () => void) {
    const [currentAccessToken, setCurrentAccessToken] = useState<string>();
    // Reactive authentication state that components can depend on
    const [isAuthenticatedState, setIsAuthenticatedState] = useState<boolean>(() => {
        return !!stringGetter(ACCESS_TOKEN_KEY) && !!stringGetter(REFRESH_TOKEN_KEY);
    });

    // Use ref to track if interceptor is already set up (prevents duplicates on re-renders)
    const interceptorIdRef = useRef<number | null>(null);

    const getAccessToken = useCallback(() => {
        return stringGetter(ACCESS_TOKEN_KEY);
    }, []);

    const getRefreshToken = useCallback(() => {
        return stringGetter(REFRESH_TOKEN_KEY);
    }, []);

    const isAuthenticated = useCallback(() => {
        return !!getAccessToken() && !!getRefreshToken();
    }, [getAccessToken, getRefreshToken]);

    const setAccessToken = useCallback((token: string) => {
        setDataToLocalStorage(ACCESS_TOKEN_KEY, token);
        setCurrentAccessToken(token);
        setIsAuthenticatedState(true);
    }, []);

    const setTokens = useCallback(({ accessToken, refreshToken }: TokenResponse) => {
        setDataToLocalStorage(ACCESS_TOKEN_KEY, accessToken);
        setDataToLocalStorage(REFRESH_TOKEN_KEY, refreshToken);
        setCurrentAccessToken(accessToken);
        setIsAuthenticatedState(true);
    }, []);

    const clearToken = useCallback(
        async (shouldClearSession = false) => {
            const clearSession =
                shouldClearSession && isAuthenticated() ? logout() : Promise.resolve();
            try {
                return await clearSession;
            } finally {
                removeDataToLocalStorage(ACCESS_TOKEN_KEY);
                removeDataToLocalStorage(REFRESH_TOKEN_KEY);
                setCurrentAccessToken("");
                setIsAuthenticatedState(false);
                onTokenInvalid();
            }
        },
        [onTokenInvalid, isAuthenticated]
    );

    const updateCurrentAccessToken = useCallback(() => {
        setCurrentAccessToken(getAccessToken() ?? undefined);
        setIsAuthenticatedState(isAuthenticated());
    }, [getAccessToken, isAuthenticated]);

    useEffect(() => {
        updateCurrentAccessToken();
    }, [updateCurrentAccessToken]);

    useEffect(() => {
        // If interceptor already exists, don't add another
        if (interceptorIdRef.current !== null) {
            return;
        }

        const retryQueueList: RetryQueueItem[] = [];
        let isRefreshing = false;

        const interceptorId = APIHandler.interceptors.response.use(
            response => response,
            async error => {
                // Only handle 401 errors
                if (!error.response || error.response.status !== 401) {
                    return Promise.reject(error);
                }

                // Discriminate user-level 401 from token-level 401. A user-level
                // 401 (e.g. "User not found") will NOT be fixed by a fresh
                // Firebase token — refreshing in a loop here is what triggered
                // the QUOTA_EXCEEDED storm during the v2.0.9 RSD19901990 incident.
                if (!isRefreshableAuthFailure(error)) {
                    console.log(
                        "[useFirebaseToken] 401 looks user-level, not refreshing:",
                        error.response?.data?.message
                    );
                    return Promise.reject(error);
                }

                // Idempotency: if THIS request has already been retried after a
                // refresh and still got 401, the underlying problem isn't a
                // stale token. Don't refresh again — propagate to the caller.
                const originalRequest: AxiosRequestConfig & {
                    [REFRESH_RETRY_FLAG]?: boolean;
                } = error.config;
                if (originalRequest[REFRESH_RETRY_FLAG]) {
                    console.log(
                        "[useFirebaseToken] Already retried this request after refresh; rejecting"
                    );
                    return Promise.reject(error);
                }

                console.log("[useFirebaseToken] 401 received, checking auth state...");

                // Check if we have tokens in localStorage
                const currentRefreshToken = getRefreshToken();
                const hasTokens = isAuthenticated();
                console.log("[useFirebaseToken] hasTokens:", hasTokens);

                // If no tokens, just reject - user isn't logged in
                if (!hasTokens || !currentRefreshToken) {
                    console.log("[useFirebaseToken] No tokens, rejecting");
                    return Promise.reject(error);
                }

                if (!isRefreshing) {
                    isRefreshing = true;
                    try {
                        console.log("[useFirebaseToken] Attempting token refresh...");

                        let newTokens: { accessToken: string; refreshToken: string };

                        // Try Firebase client SDK first (if session exists)
                        if (auth.currentUser) {
                            console.log("[useFirebaseToken] Using Firebase SDK refresh...");
                            const accessToken = await auth.currentUser.getIdToken(true);
                            newTokens = {
                                accessToken,
                                refreshToken: auth.currentUser.refreshToken
                            };
                        } else {
                            // No Firebase session - use REST API
                            console.log("[useFirebaseToken] Using REST API refresh...");
                            const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
                            if (!apiKey) {
                                throw new Error("Firebase API key not configured");
                            }
                            newTokens = await refreshTokenViaRestApi(currentRefreshToken, apiKey);
                        }

                        console.log("[useFirebaseToken] Token refreshed successfully");
                        setTokens(newTokens);

                        // Retry queued requests — mark each so a still-401
                        // response cannot kick off another refresh cycle.
                        console.log("[useFirebaseToken] Retrying queued requests...");
                        retryQueueList.forEach(({ config, resolve, reject }) => {
                            (config as any)[REFRESH_RETRY_FLAG] = true;
                            APIHandler.request(config)
                                .then(response => resolve(response))
                                .catch(err => reject(err));
                        });
                        retryQueueList.length = 0;

                        // Retry original request
                        console.log("[useFirebaseToken] Retrying original request...");
                        originalRequest[REFRESH_RETRY_FLAG] = true;
                        return APIHandler(originalRequest);
                    } catch (refreshError) {
                        console.error("[useFirebaseToken] Token refresh failed:", refreshError);
                        await clearToken();
                        // Reject all queued requests
                        retryQueueList.forEach(({ reject }) => reject(refreshError));
                        retryQueueList.length = 0;
                        throw refreshError;
                    } finally {
                        isRefreshing = false;
                    }
                }

                // Another refresh is in progress - queue this request
                return new Promise<void>((resolve, reject) => {
                    retryQueueList.push({
                        config: originalRequest,
                        resolve,
                        reject
                    });
                });
            }
        );

        interceptorIdRef.current = interceptorId;

        // Cleanup: eject interceptor when component unmounts or deps change
        return () => {
            if (interceptorIdRef.current !== null) {
                APIHandler.interceptors.response.eject(interceptorIdRef.current);
                interceptorIdRef.current = null;
            }
        };
    }, [auth, isAuthenticated, getRefreshToken, clearToken, setTokens]);

    return {
        currentAccessToken,
        isAuthenticated,
        isAuthenticatedState,
        setAccessToken,
        setTokens,
        clearToken,
        updateCurrentAccessToken
    };
}
